/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState, useCallback } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { Dropdown } from 'antd'

import { Flex } from '../../Layout'
import OptionButtonIcon from './Icon'

/**
 * Typed for TS callers, which otherwise infer every prop as required.
 * @param {object} props
 * @param {() => void} props.onClick
 * @param {import('react').ReactNode} props.title
 * @param {string} [props.iconName]
 * @param {import('react').ReactNode} [props.icon]
 * @param {string} [props.textColor]
 * @param {string} [props.iconColor]
 * @param {string} [props.faType]
 */
export const MoreOptionListItem = ({
  onClick,
  iconName,
  // Optional rendered icon (e.g. an antd icon) used instead of the Font
  // Awesome `iconName`, for an action that must match an icon used elsewhere.
  icon,
  title,
  // Optional — MoreOptions passes these for its own rows, but a caller
  // rendering a single item directly can rely on the same defaults.
  textColor = 'white',
  iconColor = 'white',
  faType = 'fas',
}) => {
  return (
    <Flex
      className='more-option-list-item'
      alignItems='center'
      onClick={onClick}
      css={css`
        background-color: ${colors.HEADER};
        padding: 12px 20px;

        cursor: pointer;

        p,
        i,
        .more-option-icon {
          transition: color 0.15s ease-in-out;
          color: ${textColor};
        }

        transition: background-color 0.15s ease-in-out;

        &:hover {
          background-color: ${colors.SOFT_STEEL};

          p,
          i,
          .more-option-icon {
            color: white;
          }
        }
      `}
    >
      {icon ? (
        <span
          className='more-option-icon'
          aria-hidden='true'
          css={css`
            display: inline-flex;
            margin-right: 8px;
            font-size: 18px;
          `}
        >
          {icon}
        </span>
      ) : (
        <OptionButtonIcon
          name={iconName}
          title={title}
          faType={faType}
          color={iconColor}
          css={css`
            margin-right: 8px;
          `}
        />
      )}
      <p
        css={css`
          text-transform: capitalize;
        `}
      >
        {title}
      </p>
    </Flex>
  )
}

const dropdownMenuStyle = css`
  width: max-content;
  border: 1px solid ${colors.SOFT_STEEL};
  box-shadow: -4px 4px 4px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  border-radius: 4px;
  background-color: ${colors.HEADER};

  .more-option-list-item {
    &:not(:last-of-type) {
      border-bottom: 1px solid ${colors.SOFT_STEEL};
    }
  }
`

const dropdownOverlayStyle = { zIndex: 9999 }

const MoreOptions = ({ options: _options = [] }) => {
  const [isOpen, setIsOpen] = useState(false)
  const options = _options.filter((opt) => {
    // Option can resolve to undefined (e.g. did not pass permission check)
    // so filter those out here
    if (typeof opt !== 'object') {
      return false
    }
    return Object.values(opt)?.length > 0
  })

  const closeDropdown = useCallback(() => setIsOpen(false), [])

  const renderDropdownContent = useCallback(
    () => (
      <div css={dropdownMenuStyle}>
        {options.map((option) => {
          const {
            key,
            title,
            onClick,
            iconName,
            icon,
            iconColor = 'white',
            textColor = 'white',
            Component,
            componentProps,
          } = option

          if (Component) {
            return (
              <Component
                key={key}
                option={option}
                onToggle={closeDropdown}
                {...componentProps}
              />
            )
          }

          return (
            <MoreOptionListItem
              key={key}
              title={title}
              onClick={() => {
                closeDropdown()
                onClick()
              }}
              iconName={iconName}
              icon={icon}
              iconColor={iconColor}
              textColor={textColor}
            />
          )
        })}
      </div>
    ),
    [options, closeDropdown]
  )

  if (options.length === 0) {
    return false
  }

  return (
    <Dropdown
      open={isOpen}
      onOpenChange={setIsOpen}
      placement='bottomRight'
      trigger={['click']}
      dropdownRender={renderDropdownContent}
      overlayStyle={dropdownOverlayStyle}
    >
      <button
        type='button'
        className='option-button-delete-btn'
        css={css`
          width: 20px;

          ${isOpen &&
          `
            i {
              color: white;
            }
          `}

          &:hover {
            i {
              color: white;
            }
          }
        `}
      >
        <OptionButtonIcon name='ellipsis-v' title='More options' />
      </button>
    </Dropdown>
  )
}

export default MoreOptions
